import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useParams,
  useSearchParams,
} from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute, NonAuthenticatedRoute } from '@/app/constant';
import { Body } from '@/components/ui/Body';
import { useGetCollaborators } from '@/hooks/collaborators';
import { useGetCollaboratorEmails } from '@/hooks/useAccounts';
import { useFlag } from '@/hooks/useFlags';
import { useMy } from '@/hooks/useMy';
import { Agents } from '@/sections/agents';
import { Assets } from '@/sections/asset';
import { Attacks } from '@/sections/attacks';
import { AuthenticatedApp } from '@/sections/AuthenticatedApp';
import AWSMarketplace from '@/sections/AWSMarketplace';
import { CustomerManagement } from '@/sections/customerManagement';
import { PraetorianUserSettings } from '@/sections/customerManagement/settings/user';
import Files from '@/sections/Files';
import Hello from '@/sections/Hello';
import { Insights } from '@/sections/insights';
import Integrations from '@/sections/Integrations';
import Jobs from '@/sections/jobs';
import { LoginWithKeychainLink } from '@/sections/loginWithKeychainLink/LoginWithKeychainLink';
import Logout from '@/sections/Logout';
import { Profile } from '@/sections/profile';
import { Seeds } from '@/sections/seeds';
import { Settings } from '@/sections/settings';
import { ForgotPassword, Login, Signup } from '@/sections/signup';
import { LoginSSO } from '@/sections/signup/LoginSSO';
import { Techonologies } from '@/sections/technology';
import { ThreatIntelComponent } from '@/sections/threats';
import { Validations } from '@/sections/validations';
import { VulnerabilityRoute } from '@/sections/vulnerabilities';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { encode } from '@/utils/encrypt.util';
import { validateRedirectUrl } from '@/utils/redirect.util';

/**
 * Determines the default route based on user type and tenant access.
 * Praetorian users and multi-tenant customers go to Customer/Tenant Management.
 */
function getDefaultRoute(
  isPraetorianUser: boolean,
  me: string,
  collaboratorEmails: string[]
): AuthenticatedRoute {
  const hasMultiTenantAccess =
    !isPraetorianUser &&
    collaboratorEmails.filter(email => email !== me).length > 1;

  return isPraetorianUser || hasMultiTenantAccess
    ? AuthenticatedRoute.CUSTOMER_MANAGEMENT
    : AuthenticatedRoute.INSIGHTS;
}

function FallbackRoute() {
  const { lastVisitedUserId } = useGlobalState();
  const {
    me,
    isSignedIn,
    logout,
    isPraetorianUser,
    pendingMfaSignIn,
    ssoDomain,
  } = useAuth();

  const defaultUserId = lastVisitedUserId || (isPraetorianUser ? me : '');
  const [userId, setUserId] = useState(defaultUserId);

  const { enablePersonalTenancy, isFlagLoading } = useFlag({
    doNotImpersonate: true,
  });
  const { data: collaborators = [], status } = useGetCollaborators({
    doNotImpersonate: true,
  });

  useEffect(() => {
    if (!defaultUserId && !isFlagLoading) {
      if (enablePersonalTenancy || ssoDomain) {
        setUserId(me);
      } else {
        if (status !== 'pending') {
          // Multi-tenant users (>1 non-own tenant) use own email for customer management
          const nonOwnTenants = collaborators.filter(
            c => c.email !== me
          );
          if (nonOwnTenants.length > 1) {
            setUserId(me);
          } else {
            const mostRecentCollaborator = collaborators
              ?.slice?.()
              ?.sort((a, b) => {
                const dateA = a.updated
                  ? new Date(a.updated).getTime()
                  : 0;
                const dateB = b.updated
                  ? new Date(b.updated).getTime()
                  : 0;
                return dateB - dateA;
              })?.[0];

            if (mostRecentCollaborator?.email) {
              setUserId(mostRecentCollaborator.email);
            } else {
              toast.error(
                'No collaborators found, please contact support@praetorian.com'
              );
              logout();
            }
          }
        }
      }
    }
  }, [status, collaborators, isFlagLoading]);

  if (isSignedIn) {
    if (pendingMfaSignIn) {
      toast.error('MFA setup required, please login again');
      logout();
      return null;
    } else if (userId) {
      const defaultRoute = getDefaultRoute(
        isPraetorianUser,
        me,
        collaborators.map(c => c.email)
      );

      return (
        <Navigate
          to={generatePath(`:userId/${defaultRoute}`, {
            userId: encode(userId),
          })}
          replace
        />
      );
    } else {
      // Return null while determining userId - avoids flash of transition screen
      return null;
    }
  } else {
    return <Navigate to={NonAuthenticatedRoute.LOGIN} replace />;
  }
}

function CheckAuth(props: { children: ReactNode }) {
  const { lastVisitedUserId } = useGlobalState();
  const { userId } = useParams();
  const {
    isSignedIn,
    userIdFromUrl,
    me,
    pendingMfaSignIn,
    logout,
    isPraetorianUser,
  } = useAuth();

  const isOwner = userIdFromUrl === me;

  const { data: accounts, status: accountsStatus } = useMy(
    {
      resource: 'account',
    },
    {
      doNotImpersonate: true,
      enabled: isSignedIn && !isOwner,
    }
  );
  const collaboratorEmails = useGetCollaboratorEmails(accounts);

  if (isSignedIn) {
    if (pendingMfaSignIn) {
      toast.error('MFA setup required, please login again');
      logout();
      return null;
    }
    /* mapping for old route */
    if (userId === 'app') {
      const appRoute = location.pathname.replace('/app/', '');

      const routeMap: Record<string, AuthenticatedRoute> = {
        '/app/account': AuthenticatedRoute.SETTINGS,
        '/app/overview': AuthenticatedRoute.INTEGRATIONS,
        '/app/risks': AuthenticatedRoute.VULNERABILITIES,
      };

      return (
        <Navigate
          to={`/${generatePath(
            `:userId/${routeMap[location.pathname] || appRoute}`,
            {
              userId: encode(lastVisitedUserId || me),
            }
          )}${window.location.search}`}
          replace
        />
      );
    }

    if (isOwner) {
      return props.children;
    } else {
      if (accountsStatus !== 'pending') {
        const isUserACollaboratorOfImpersonatedAccount =
          collaboratorEmails.find(email => email === userIdFromUrl);

        if (isUserACollaboratorOfImpersonatedAccount) {
          return props.children;
        } else {
          const defaultRoute = getDefaultRoute(
            isPraetorianUser,
            me,
            collaboratorEmails
          );

          return (
            <Navigate
              to={generatePath(`/:userId/${defaultRoute}`, {
                userId: encode(me),
              })}
              replace
            />
          );
        }
      } else {
        // We are checking if the user is a collaborator, so we don't need to show anything while loading
        return null;
      }
    }
  } else {
    // Encode the current URL (path + search + hash) as a query parameter
    // Include hash in case drawer params were incorrectly parsed as fragment
    // This survives OAuth page reloads and eliminates timing issues
    const fullUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirectUrl = encodeURIComponent(fullUrl);
    const loginUrl = `${NonAuthenticatedRoute.LOGIN}?redirect=${redirectUrl}`;
    return <Navigate to={loginUrl} replace />;
  }
}

function CheckNoAuth(props: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();

  if (!isSignedIn) {
    return props.children;
  } else {
    // Read redirect URL from query params (set by CheckAuth)
    // NOTE: Do NOT use decodeURIComponent() - it would decode %23 to #
    // which the browser interprets as fragment delimiter, breaking drawer params
    const rawDestination = searchParams.get('redirect') || '/';

    // SECURITY: Use centralized validation utility to prevent open redirect (CWE-601)
    const destination = validateRedirectUrl(rawDestination);

    return <Navigate to={destination} replace />;
  }
}

function AuthenticatedFallback() {
  const { userId } = useParams();
  const { isPraetorianUser, me } = useAuth();

  const { data: accounts = [] } = useMy(
    { resource: 'account' },
    { doNotImpersonate: true, enabled: !isPraetorianUser }
  );
  const collaboratorEmails = useGetCollaboratorEmails(accounts);
  const defaultRoute = getDefaultRoute(isPraetorianUser, me, collaboratorEmails);

  return (
    <Navigate
      to={generatePath(`/:userId/${defaultRoute}`, { userId: userId || '' })}
      replace
    />
  );
}

export function AppRoutes({ children }: { children: ReactNode }) {
  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={children}>
            <Route path="" element={<FallbackRoute />} />
            <Route
              path={NonAuthenticatedRoute.MARKETPLACE_AWS}
              element={<AWSMarketplace />}
            />
            <Route
              path={NonAuthenticatedRoute.LOGIN_WITH_KEYCHAIN}
              element={<LoginWithKeychainLink />}
            />
            <Route
              path=":userId"
              element={
                <CheckAuth>
                  <AuthenticatedApp>
                    <Body>
                      <Outlet />
                    </Body>
                  </AuthenticatedApp>
                </CheckAuth>
              }
            >
              <Route
                path={AuthenticatedRoute.INSIGHTS}
                element={<Insights />}
              />
              <Route path={AuthenticatedRoute.SEEDS} element={<Seeds />} />
              <Route path={AuthenticatedRoute.LOGOUT} element={<Logout />} />
              <Route path={AuthenticatedRoute.ASSETS} element={<Assets />} />
              <Route
                path={AuthenticatedRoute.RED_TEAM}
                element={
                  <Navigate
                    to={`${AuthenticatedRoute.ATTACKS}?tab=red-team`}
                    replace
                  />
                }
              />
              <Route path={AuthenticatedRoute.JOBS} element={<Jobs />} />
              <Route
                path={AuthenticatedRoute.SETTINGS}
                element={<Settings />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_SCAN}
                element={<Settings defaultTab="scanSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_NOTIFICATIONS}
                element={<Settings defaultTab="notificationSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_ORGANIZATION}
                element={<Settings defaultTab="orgSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_USER_MANAGEMENT}
                element={<Settings defaultTab="userManagement" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_ACCESS_LOGS}
                element={<Settings defaultTab="accessLogs" />}
              />
              {/* User Profile is a separate page, accessible from AccountDropdown */}
              <Route path={AuthenticatedRoute.PROFILE} element={<Profile />} />
              <Route
                path={AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS}
                element={<PraetorianUserSettings />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_MONITORING}
                element={<Settings defaultTab="monitoring" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_SUBSCRIPTION}
                element={<Settings defaultTab="subscription" />}
              />
              <Route path={AuthenticatedRoute.FILES} element={<Files />} />
              <Route
                path={AuthenticatedRoute.USERS}
                element={
                  <Navigate
                    to={AuthenticatedRoute.SETTINGS_USER_MANAGEMENT}
                    replace
                  />
                }
              />
              <Route path={AuthenticatedRoute.AGENTS} element={<Agents />} />
              <Route
                path={AuthenticatedRoute.TECHNOLOGIES}
                element={<Techonologies />}
              />
              <Route
                path={AuthenticatedRoute.INTEGRATIONS}
                element={<Integrations />}
              />
              <Route
                path={AuthenticatedRoute.CAPABILITIES}
                element={
                  <Navigate
                    to={`${AuthenticatedRoute.ATTACKS}?tab=capabilities`}
                    replace
                  />
                }
              />
              <Route
                path={AuthenticatedRoute.VULNERABILITIES}
                element={<VulnerabilityRoute />}
              />
              <Route path={AuthenticatedRoute.ATTACKS} element={<Attacks />} />
              <Route
                path={AuthenticatedRoute.VALIDATIONS}
                element={<Validations />}
              />
              {/* Redirect old URL for backward compatibility */}
              <Route
                path="threat-intel"
                element={<Navigate to={AuthenticatedRoute.THREATS} replace />}
              />
              <Route
                path={AuthenticatedRoute.THREATS}
                element={<ThreatIntelComponent />}
              />
              <Route
                path={AuthenticatedRoute.CUSTOMER_MANAGEMENT}
                element={<CustomerManagement />}
              />
              <Route path="" element={<AuthenticatedFallback />} />
              <Route path="*" element={<AuthenticatedFallback />} />
            </Route>
            <Route
              element={
                <CheckNoAuth>
                  <Outlet />
                </CheckNoAuth>
              }
            >
              <Route path={NonAuthenticatedRoute.LOGIN} element={<Login />} />
              <Route
                path={NonAuthenticatedRoute.LOGIN_SSO}
                element={<LoginSSO />}
              />
              <Route path={NonAuthenticatedRoute.SIGNUP} element={<Signup />} />
              <Route
                path={NonAuthenticatedRoute.FORGOT_PASSWORD}
                element={<ForgotPassword />}
              />
              <Route path={NonAuthenticatedRoute.HELLO} element={<Hello />} />
              <Route
                path={NonAuthenticatedRoute.GOODBYE}
                element={<Navigate to={NonAuthenticatedRoute.LOGIN} replace />}
              />
              <Route path="*" element={<FallbackRoute />} />
            </Route>
          </Route>
        )
      ),
    [children]
  );

  return <RouterProvider router={router} />;
}
